import { SubFilter, IWidget, IWidgetDefinition } from '../../common/core';
import { IDropdownFacetSettings, IDropdownFacetState, SortOption } from './settings';

/**
* shorter name for IWidget<X,Y,Z>
*/
export interface IDropdownFacetWidget extends IWidget<DropdownFacetData, IDropdownFacetSettings, IDropdownFacetState> { }


export class DropdownFacetData {
    terms = [];
    data = [];
    theme?: string;

    constructor(private widget: IDropdownFacetWidget) {
        if (widget.settings.update === false) {
            this.requestFacets(widget.settings.maxFacets || 20, (data) => {
                this.setTerms(this.getResponseTerms(data));
            });
        }
        this.theme = this.widget.service.getGlobalOptions().theme;
    }

    public getResponseTerms(response) {
        var facets = response.facets || {};
        var facet: any = facets[this.fieldWithSort] || {};
        return facet.terms || [];
    }

    /**
     * Get global query promise
     * Can be overridden in widgets
     */
    public getGlobalQuery(): Promise<string> {
        return this.widget.service.awaitGlobalQuery(this.widget, true, false);
    }

    requestFacets(size, callback) {
        this.getGlobalQuery().then(q => {

            var request = {
                facets: this.fieldWithSort,
                query: q,
                max_facet_values: size,
                per_page: 0
            };

            var default_fields = this.widget.service.seriesParam.default_fields;
            if (default_fields) {
                if (typeof default_fields == 'function') {
                    default_fields = default_fields(this.widget.service);
                }
                request['default_fields'] = default_fields;
            }

            this.widget.service.sjclient.raw.get('/series', request, callback);
        });
    }

    setTerms(terms) {
        if (this.widget.settings.adjustFacets) {
            this.terms = this.widget.settings.adjustFacets(this.widget, terms);
        } else {
            this.terms = terms;
        }
    }

    getField() {
        return this.widget.settings.field;
    }

    onSelect() {
        this.widget.service.setState();
    }

    getSelectedQuery() {
        if (this.widget.state.selectedValue) {
            return this.widget.service.helpers.formatQueryPart(this.getField(), this.widget.state.selectedValue, '=');
        }
    }

    get fieldWithSort() {
        let sort = '';
        if (this.widget.settings.sort && this.widget.settings.sort !== 'count.desc') {
            sort = `@sort:${this.widget.settings.sort}`;
        }

        return `${this.getField()}${sort}`;
    }
}

var widgetDefinition: IWidgetDefinition<DropdownFacetData, IDropdownFacetSettings, IDropdownFacetState> = {
    defaultState: {
        selectedValue: ''
    },
    init(widget) {
        widget.data = new DropdownFacetData(widget);
    },
    getQuery(widget) {
        return widget.data.getSelectedQuery();
    },
    beforeSearch(widget) {
        if (widget.settings.update !== false) {
            widget.data.requestFacets(widget.settings.maxFacets || 20, (data) => {
                widget.data.setTerms(widget.data.getResponseTerms(data));
            });
        }
    },
    getSubFilters(widget) {
        var defaultValue = (widget['definition'].defaultState || {}).selectedValue || '';
        if (widget.state.selectedValue && widget.state.selectedValue != defaultValue) {
            var terms = widget.data.terms || [];
            var res = {
                value: widget.state.selectedValue,
                widgetId: widget.id,
                widgetLabel: widget.settings.label || ''
            }
            Object.defineProperty(res, 'label', {
                get() {
                    if (!this._selectedTerm) {
                        this._selectedTerm = terms.find(t => t.term == widget.state.selectedValue);
                    }
                    if (this._selectedTerm) {
                        return this._selectedTerm.label || this._selectedTerm.term || widget.state.selectedValue;
                    }
                    return widget.state.selectedValue;
                }
            });
            return [res];
        }
        return null;
    },
    removeSubFilter(widget, filter: SubFilter) {
        var defaultValue = (widget['definition'].defaultState || {}).selectedValue || '';
        if (widget.state.selectedValue && widget.state.selectedValue != defaultValue) {
            widget.state.selectedValue = defaultValue;
        }
    },
    template: `
        <div class="dropdown-facet" [ngClass]="widget.settings.className || 'sj-dropdown-facet'"
            *ngIf="widget.settings.field">
            <label *ngIf='widget.settings.label' [innerHTML]='widget.settings.label'></label>

            <select [attr.multiple]="widget.settings.multiple" 
              *ngIf='!widget.data.theme'
              [(ngModel)]="widget.state.selectedValue" (change)="widget.data.onSelect()">
                <option *ngIf="widget.settings.allItem" [value]="widget.settings.allItem.value || ''" selected>{{widget.settings.allItem.text}}</option>
                <option *ngFor="let t of widget.data.terms" [value]="t.term">{{t.label || t.term}}</option>
            </select>

            <div *ngIf='widget.data.theme'>
                <div class="sj-input" [ngClass]="widget.settings.selectClassName || 'sj-input-xs sj-inline'">
                    <select [attr.multiple]="widget.settings.multiple" 
                        [(ngModel)]="widget.state.selectedValue" (change)="widget.data.onSelect()">
                        <option *ngIf="widget.settings.allItem" [value]="widget.settings.allItem.value || ''" selected>{{widget.settings.allItem.text}}</option>
                        <option *ngFor="let t of widget.data.terms" [value]="t.term">{{t.label || t.term}}</option>
                    </select>
                </div>
            </div>
        </div>

        <div class="sj-widget-setup sj-widget-setup-small sj-section" *ngIf="!widget.settings.field">
            <div class="sj-setup-container">
                <div class="sj-setup-icon">
                <svg width="50" height="44" viewBox="0 0 50 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M28.4642 2C26.9246 -0.666665 23.0756 -0.666668 21.5359 2L0.751339 38C-0.788262 40.6667 1.13624 44 4.21544 44H45.7847C48.8639 44 50.7884 40.6667 49.2488 38L28.4642 2ZM26.6211 30.5781L27.0782 19H23.1875L23.6211 30.5781H26.6211ZM23.6094 35.5117C24.0157 35.8867 24.5313 36.0742 25.1563 36.0742C25.7891 36.0742 26.3008 35.8906 26.6915 35.5234C27.0899 35.1562 27.2891 34.6914 27.2891 34.1289C27.2891 33.5742 27.0899 33.1172 26.6915 32.7578C26.293 32.3906 25.7813 32.207 25.1563 32.207C24.5391 32.207 24.0235 32.3867 23.6094 32.7461C23.2032 33.1055 23 33.5664 23 34.1289C23 34.668 23.2032 35.1289 23.6094 35.5117Z" fill="#F2994A"/>
                </svg>
                </div>
                <div>
                <div class="sj-setup-title">
                    Widget not configured
                </div>
                <div class="sj-setup-actions">
                    <div (click)="widget.widget.sendMessageToEditor({ event: 'edit' })">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M1 0C0.447715 0 0 0.447715 0 1V13C0 13.5523 0.447715 14 1 14H13C13.5523 14 14 13.5523 14 13V1C14 0.447715 13.5523 0 13 0H1ZM2 12V2H12V12H2ZM5 3H3V5H5V3ZM3 6H5V8H3V6ZM5 9H3V11H5V9ZM6 3H8V5H6V3ZM8 6H6V8H8V6ZM6 9H8V11H6V9ZM11 3H9V5H11V3ZM9 6H11V8H9V6ZM11 9H9V11H11V9Z" fill="#4A90E2"/>
                        </svg>
                        <span>Edit</span>
                    </div>
                </div>
                </div>
            </div>
        </div>
    `,
    styles: `
        .dropdown-facet > label {
            float: left;
        }
        .sj-theme-shooju-22 .dropdown-facet.sj-dropdown-facet label {
            display: block;
            font-weight: 700;
            font-size: 14px;
            line-height: 23px;
            color: #6C747C;
            float: none;
        }
    `
};
export default widgetDefinition;